<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: HorizontalAncestorChart                               |
| Section: views/trees                                             |
| Purpose: Horizontal ancestor chart route for lineage traversal.  |
+------------------------------------------------------------------+
-->
<template>
    <!-- The chart container fills the viewport -->
    <div ref="chartContainer" style="width: 100vw; height: 100vh;"></div>
</template>

<script>
import * as d3 from 'd3';

export default {
    name: 'HorizontalAncestorChart',
    // Inject properties to check if the user is logged in.
    inject: ['authToken', 'username', 'logout'],
    props: {
        personId: {
            type: String,
            required: true
        },
        // The number of generations to show (controlled via generationSelect)
        selectedGenerations: {
            type: Number,
            default: 6
        }
    },
    data() {
        return {
            ancestorsData: null
        };
    },
    mounted() {
        this.fetchData();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    watch: {
        // Re-render when the generation count changes.
        selectedGenerations() {
            this.renderChart();
        }
    },
    methods: {
        fetchData() {
            fetch(`${process.env.VUE_APP_BACKEND_API_URL}/ancestors/${this.personId}`, {
                headers: {
                    'X-API-KEY': process.env.VUE_APP_API_KEY
                }
            })
                .then(response => {
                    if (!response.ok) throw new Error('Network response was not ok');
                    return response.json();
                })
                .then(data => {
                    this.ancestorsData = data;
                    this.renderChart();
                })
                .catch(error => console.error('Error loading data:', error));
        },
        onResize() {
            this.renderChart();
        },
        renderChart() {
            if (!this.ancestorsData) return;

            // Clear any previous chart content.
            if (this.$refs.chartContainer) {
                this.$refs.chartContainer.innerHTML = "";
            }

            // Define margins so the tree does not cramp up against the viewport edges.
            const margin = { top: 192, right: 192, bottom: 192, left: 192 };

            const viewportWidth = window.innerWidth;
            // Calculate the available width after subtracting left/right margins.
            const availableWidth = viewportWidth - margin.left - margin.right;

            // Create a hierarchy from the data.
            const root = d3.hierarchy(this.ancestorsData, d => d.ancestors);

            // Prune the hierarchy to limit the displayed generations.
            const maxDepth = this.selectedGenerations - 1;
            function prune(node, maxDepth) {
                if (node.depth >= maxDepth) {
                    node.children = null;
                } else if (node.children) {
                    node.children.forEach(child => prune(child, maxDepth));
                }
            }
            prune(root, maxDepth);

            // Increase dx to spread out the nodes vertically.
            const dx = 20;
            // Horizontal spacing is determined by the available width and the number of generations.
            const dy = availableWidth / maxDepth;

            // Create the tree layout.
            const tree = d3.tree().nodeSize([dx, dy]);
            root.sort((a, b) => d3.ascending(a.data.name, b.data.name));
            tree(root);

            // Compute the vertical extent of the tree.
            let x0 = Infinity, x1 = -Infinity;
            root.each(d => {
                if (d.x > x1) x1 = d.x;
                if (d.x < x0) x0 = d.x;
            });
            const treeHeight = x1 - x0 + dx * 2;
            const totalHeight = treeHeight + margin.top + margin.bottom;
            // Ensure the SVG is at least as tall as the viewport.
            const svgHeight = Math.max(totalHeight, window.innerHeight);

            // Create the SVG element.
            const svg = d3.select(this.$refs.chartContainer)
                .append("svg")
                .attr("width", viewportWidth)
                .attr("height", svgHeight)
                .attr("viewBox", [-margin.left, x0 - dx - margin.top, viewportWidth, svgHeight])
                .attr("preserveAspectRatio", "xMidYMid meet")
                .attr("style", "max-width: 100%; height: auto; font: 10px sans-serif;");

            // Draw links (lines) between nodes.
            svg.append("g")
                .attr("fill", "none")
                .attr("stroke", "#555")
                .attr("stroke-opacity", 0.4)
                .attr("stroke-width", 1.5)
                .selectAll("path")
                .data(root.links())
                .join("path")
                .attr("d", d3.linkHorizontal()
                    .x(d => d.y)
                    .y(d => d.x));

            // Draw the nodes.
            const node = svg.append("g")
                .attr("stroke-linejoin", "round")
                .attr("stroke-width", 3)
                .selectAll("g")
                .data(root.descendants())
                .join("g")
                .attr("transform", d => `translate(${d.y},${d.x})`);

            // Append a circle for each node (disable pointer events so that links are clickable).
            node.append("circle")
                .attr("fill", "var(--brand-color-3)")
                .attr("r", 2.5)
                .style("pointer-events", "none");

            // Helper functions to determine the text x-offset and anchor.
            function getX() {
                return -6;
            }
            function getAnchor() {
                return "end";
            }

            // Capture the auth token for use in our text function.
            const authToken = this.authToken;

            // Append clickable links.
            const clickableLink = node.append("a")
                .attr("xlink:href", d => d.data.placeholder ? null : `/person/${d.data.personID}`)
                .attr("href", d => d.data.placeholder ? null : `/person/${d.data.personID}`)
                .style("pointer-events", "all");

            // Append the text element with a class for hover effects.
            clickableLink.append("text")
                .attr("class", "clickable-text")
                .attr("dy", "0.31em")
                .attr("x", d => getX(d))
                .attr("text-anchor", d => getAnchor(d))
                .text(d => {
                    // If the person is marked as living and there's no authToken (not logged in),
                    // show the privacy message; otherwise show the name.
                    if (d.data.placeholder) {
                        return this.$t('privacy.livingPlaceholder');
                    }
                    if (d.data.living === 1 && !authToken) {
                        return this.$t('nav.privacyHidden');
                    }
                    return d.data.name || `${d.data.firstname || ''} ${d.data.lastname || ''}`.trim();
                })
                .attr("fill", "white")
                .attr("paint-order", "stroke")
                .style("pointer-events", "all")
                .style("font-family", "var(--font-primary)")
                .style("font-size", "12px")
                .style("opacity", d => (d.data.placeholder || (d.data.living === 1 && !authToken)) ? 0.48 : 1)
                .style("font-weight", d => (d.data.placeholder || (d.data.living === 1 && !authToken)) ? 400 : 700);

            // Insert an invisible rectangle behind the text so that the clickable area is larger.
            node.each(function () {
                const a = d3.select(this).select("a");
                const textEl = a.select("text").node();
                if (textEl) {
                    const bbox = textEl.getBBox();
                    a.insert("rect", "text")
                        .attr("x", bbox.x)
                        .attr("y", bbox.y)
                        .attr("width", bbox.width)
                        .attr("height", bbox.height)
                        .style("fill", "transparent")
                        .style("pointer-events", "all");
                }
            });
        }
    }
};
</script>

<style scoped>
.clickable-text:hover {
    text-decoration: underline;
}
</style>
